<template>
  <div class="flex flex-col gap-1.5">
    <label
      v-if="label"
      :for="selectId"
      class="block text-sm font-medium text-gray-900"
    >
      {{ label }}
    </label>
    <div class="relative">
      <select
        :id="selectId"
        :value="modelValue ?? ''"
        :disabled="disabled"
        :data-cy="dataCy"
        :class="[
          'w-full appearance-none rounded-md border bg-white pl-3 pr-9 py-2 text-sm text-gray-900',
          'focus:outline-none focus:ring-1 focus:border-blue-500 focus:ring-blue-500',
          'disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed',
          errorMessage ? 'border-red-400' : 'border-gray-300'
        ]"
        @change="
          emit('update:modelValue', ($event.target as HTMLSelectElement).value)
        "
      >
        <option v-if="placeholder" value="" disabled>{{ placeholder }}</option>
        <option
          v-for="opt in options"
          :key="String(opt.value)"
          :value="opt.value"
        >
          {{ opt.label }}
        </option>
      </select>
      <i
        class="fa-regular fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 pointer-events-none"
        aria-hidden="true"
      />
    </div>
    <span v-if="errorMessage" class="text-xs text-red-600">{{
      errorMessage
    }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed, useId } from "vue";

interface Option {
  label: string;
  value: string | number;
}

withDefaults(
  defineProps<{
    modelValue: string | number | null;
    options: Option[];
    label?: string;
    placeholder?: string;
    errorMessage?: string;
    disabled?: boolean;
    dataCy?: string;
  }>(),
  {
    label: "",
    placeholder: "",
    errorMessage: "",
    disabled: false,
    dataCy: undefined
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", value: string | number): void;
}>();

const autoId = useId();
const selectId = computed(() => `pf-select-${autoId}`);
</script>
